import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
    CalendarDaysIcon,
    DocumentTextIcon,
    FlagIcon,
    PaperClipIcon,
} from '@heroicons/react/24/outline';
import { DrawerCustom } from '../../../components/drawer/Drawer';
import { Button } from '../../../components/buttons/Button';
import { InputTextModern } from '../../../components/inputs/InputTextModern';
import { SearchableSelect } from '../../../components/searchableSelect';
import { InputDateModern } from '../../../components/inputDateModern/InputDateModern';
import { TimePickerModern } from '../../../components/timePickerModern/TimePickerModern';
import { TextareaModern } from '../../../components/textareaModern';
import { TaskAttachmentField } from './TaskAttachmentField';
import { useTaskAttachments } from '../../../hooks/useTaskAttachments';
import { createCourseTask, updateCourseTask } from '../../../redux/actions/tasks';
import {
    TASK_PRIORITY_LABELS,
    TEACHER_TASK_STATUS_LABELS,
    combineDateTime,
    extractTime,
} from './taskLabels';
import { snapToDayTimeSlot } from '../../../components/timePickerModern/timePickerUtils';
import {
    TASK_FORM_FIELD_LABELS,
    TASK_DESCRIPTION_MAX,
    buildTaskValidationToastMessage,
    createEmptyTaskFormErrors,
    validateTaskFormValues,
} from './taskFormValidation';
import { asAttachmentList, TASK_MAX_ATTACHMENTS } from '../../../common/taskUploads';
import { CourseFormGrid, CourseFormSection } from '../components/CourseFormSection';
import './task-form.css';

const SECTION_ICON_CLASS = 'h-5 w-5';

const PRIORITY_DOT_CLASS = {
    low: 'task-priority-dot task-priority-dot--low',
    medium: 'task-priority-dot task-priority-dot--medium',
    high: 'task-priority-dot task-priority-dot--high',
};

const PRIORITY_OPTIONS = Object.entries(TASK_PRIORITY_LABELS)
    .map(([value, label]) => ({
        value,
        label,
        icon: <span className={PRIORITY_DOT_CLASS[value]} aria-hidden="true" />,
    }));

const STATUS_OPTIONS = Object.entries(TEACHER_TASK_STATUS_LABELS)
    .map(([value, label]) => ({ value, label }));

const emptyForm = {
    title: '',
    description: '',
    priority: 'medium',
    status: 'pending',
    activationDate: null,
    activationTime: '08:00',
    dueDate: null,
    dueTime: '23:59',
};

export const TaskFormDrawer = ({ isOpen, onClose, courseId, task = null }) => {
    const dispatch = useDispatch();
    const isEdit = Boolean(task);

    const [form, setForm] = useState(emptyForm);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [saving, setSaving] = useState(false);
    const [formErrors, setFormErrors] = useState(createEmptyTaskFormErrors);
    const [showValidationSummary, setShowValidationSummary] = useState(false);
    const [fieldsKey, setFieldsKey] = useState(0);
    const scrollRef = useRef(null);
    const draftEntityKeyRef = useRef(null);

    const draftEntityKey = isEdit && task?._id
        ? `edit:${task._id}`
        : `create:${courseId || 'new'}`;

    const {
        items: stagedItems,
        readyPayloads,
        isUploading,
        startUploads,
        removeItem,
        clearAll,
    } = useTaskAttachments(courseId);

    const isProcessing = saving || isUploading;

    const resetValidationState = useCallback(() => {
        setFormErrors(createEmptyTaskFormErrors());
        setShowValidationSummary(false);
    }, []);

    const clearFieldError = useCallback((field) => {
        setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }, []);

    const hydrateForm = useCallback(() => {
        if (task) {
            setForm({
                title: task.title || '',
                description: task.description || '',
                priority: task.priority || 'medium',
                status: task.status === 'completed' ? 'pending' : (task.status || 'pending'),
                activationDate: task.activationDate ? new Date(task.activationDate) : null,
                activationTime: snapToDayTimeSlot(extractTime(task.activationDate) || '08:00'),
                dueDate: task.dueDate ? new Date(task.dueDate) : null,
                dueTime: snapToDayTimeSlot(extractTime(task.dueDate) || '23:59'),
            });
            setExistingAttachments(asAttachmentList(
                task.attachments?.length ? task.attachments : task.attachment,
            ));
        } else {
            setForm(emptyForm);
            setExistingAttachments([]);
        }
        clearAll();
    }, [task, clearAll]);

    const resetDraft = useCallback(() => {
        draftEntityKeyRef.current = null;
        setForm(emptyForm);
        setExistingAttachments([]);
        clearAll();
        setSaving(false);
        resetValidationState();
    }, [clearAll, resetValidationState]);

    useEffect(() => {
        if (!isOpen) return;

        resetValidationState();
        setFieldsKey((key) => key + 1);

        if (draftEntityKeyRef.current === draftEntityKey) {
            return;
        }

        draftEntityKeyRef.current = draftEntityKey;
        setSaving(false);
        hydrateForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, draftEntityKey]);

    const setField = (field) => (e) => {
        const value = e?.target ? e.target.value : e;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSelectFiles = async (files) => {
        const remaining = TASK_MAX_ATTACHMENTS
            - existingAttachments.length
            - stagedItems.filter((item) => item.status !== 'error').length;
        const result = await startUploads(files, { remainingSlots: remaining });
        if (!result.ok && result.message) {
            toast.error(result.message);
        }
    };

    const validateForm = useCallback(() => {
        const { isValid, errors } = validateTaskFormValues(form, { combineDateTime });
        setFormErrors(errors);

        if (!isValid) {
            setShowValidationSummary(true);
            const missingLabels = Object.entries(errors)
                .filter(([, msg]) => msg)
                .map(([key]) => TASK_FORM_FIELD_LABELS[key]);
            toast.error(buildTaskValidationToastMessage(missingLabels), { duration: 5000 });
            requestAnimationFrame(() => {
                scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            });
            return false;
        }

        setShowValidationSummary(false);
        return true;
    }, [form]);

    const handleDismiss = () => {
        if (isProcessing) return;
        resetValidationState();
        onClose({ reset: false });
    };

    const handleCancel = () => {
        if (isProcessing) return;
        resetDraft();
        onClose({ reset: true });
    };

    const handleSave = async () => {
        if (saving) return;
        if (!validateForm()) return;
        if (isUploading) {
            toast.error('Espera a que terminen de subirse los archivos.');
            return;
        }

        const activation = combineDateTime(form.activationDate, form.activationTime);
        const due = combineDateTime(form.dueDate, form.dueTime);

        const nextAttachments = [
            ...existingAttachments.map(({ fileName, mimeType, size, storageKey, url, provider }) => ({
                fileName,
                mimeType,
                size,
                storageKey,
                url,
                provider: provider || 'r2',
            })),
            ...readyPayloads,
        ];

        if (nextAttachments.length > TASK_MAX_ATTACHMENTS) {
            toast.error(`Puedes adjuntar máximo ${TASK_MAX_ATTACHMENTS} archivos.`);
            return;
        }

        const payload = {
            title: form.title.trim(),
            description: form.description.trim(),
            priority: form.priority,
            activationDate: activation.toISOString(),
            dueDate: due.toISOString(),
            attachments: nextAttachments,
        };

        if (isEdit) {
            payload.status = form.status;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await dispatch(updateCourseTask(task._id, payload));
                toast.success('Tarea actualizada');
            } else {
                await dispatch(createCourseTask(courseId, payload));
                toast.success('Tarea creada');
            }
            resetDraft();
            onClose({ reset: true, refresh: false });
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudo guardar la tarea.');
        } finally {
            setSaving(false);
        }
    };

    const validationErrorEntries = Object.entries(formErrors).filter(([, msg]) => msg);

    const validationSummary = showValidationSummary && validationErrorEntries.length > 0 ? (
        <div className="task-form__summary" role="alert">
            <p className="task-form__summary-title">
                {validationErrorEntries.length === 1
                    ? 'Falta un campo obligatorio'
                    : `Faltan ${validationErrorEntries.length} campos obligatorios`}
            </p>
            <ul className="task-form__summary-list">
                {validationErrorEntries.map(([key, msg]) => (
                    <li key={key}>{msg}</li>
                ))}
            </ul>
        </div>
    ) : null;

    return (
        <DrawerCustom
            isOpen={isOpen}
            toggleDrawer={handleDismiss}
            lockClose={isProcessing}
            title={isEdit ? 'Editar tarea' : 'Nueva tarea'}
            subtitle={isEdit ? task?.title : 'Los alumnos la verán a partir de la fecha de activación'}
            footer={(
                <div className="task-form__footer-actions">
                    <Button variant="outline" onClick={handleCancel} disabled={isProcessing}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        className="task-form__submit"
                        onClick={handleSave}
                        loading={saving}
                        loadingLabel="Guardando…"
                        disabled={isUploading}
                    >
                        {isEdit ? 'Guardar cambios' : 'Crear tarea'}
                    </Button>
                </div>
            )}
        >
            <div className="task-form">
                <div ref={scrollRef} className="task-form__scroll">
                    <div key={fieldsKey} className="task-form__fields">
                        {validationSummary}

                        <CourseFormSection
                            id="task-section-content"
                            title="Contenido"
                            description="Título y consignas que verán los alumnos."
                            icon={<DocumentTextIcon className={SECTION_ICON_CLASS} />}
                        >
                            <InputTextModern
                                id="task-title"
                                label="Título"
                                value={form.title}
                                onChange={(e) => {
                                    clearFieldError('title');
                                    setField('title')(e);
                                }}
                                placeholder="Ej. Proyecto final"
                                required
                                error={formErrors.title}
                            />

                            <TextareaModern
                                id="task-description"
                                name="task-description"
                                label="Descripción"
                                value={form.description}
                                onChange={(e) => {
                                    clearFieldError('description');
                                    setField('description')(e);
                                }}
                                placeholder="Describe qué deben entregar los alumnos…"
                                required
                                error={formErrors.description}
                                maxLength={TASK_DESCRIPTION_MAX}
                                minRows={4}
                            />
                        </CourseFormSection>

                        <CourseFormSection
                            id="task-section-classification"
                            title="Clasificación"
                            description="Prioridad y estado de la tarea."
                            icon={<FlagIcon className={SECTION_ICON_CLASS} />}
                        >
                            <CourseFormGrid>
                                <SearchableSelect
                                    id="task-priority"
                                    name="task-priority"
                                    label="Prioridad"
                                    value={form.priority}
                                    onChange={(value) => {
                                        clearFieldError('priority');
                                        setForm((prev) => ({ ...prev, priority: value }));
                                    }}
                                    placeholder="Selecciona prioridad"
                                    searchPlaceholder="Buscar prioridad…"
                                    options={PRIORITY_OPTIONS}
                                    required
                                    error={formErrors.priority}
                                    maxVisibleOptions={6}
                                />

                                {isEdit ? (
                                    <SearchableSelect
                                        id="task-status"
                                        name="task-status"
                                        label="Estado"
                                        value={form.status}
                                        onChange={(value) => {
                                            setForm((prev) => ({ ...prev, status: value }));
                                        }}
                                        placeholder="Selecciona estado"
                                        searchPlaceholder="Buscar estado…"
                                        options={STATUS_OPTIONS}
                                        maxVisibleOptions={6}
                                    />
                                ) : null}
                            </CourseFormGrid>
                        </CourseFormSection>

                        <CourseFormSection
                            id="task-section-schedule"
                            title="Fechas"
                            description="Cuándo se activa la tarea y hasta cuándo se puede entregar."
                            icon={<CalendarDaysIcon className={SECTION_ICON_CLASS} />}
                        >
                            <div className="task-form__datetime-block">
                                <span className="task-form__datetime-label">
                                    Fecha de activación
                                    <span className="task-form__datetime-required" aria-hidden="true">*</span>
                                </span>
                                <div className="task-form__datetime-row">
                                    <InputDateModern
                                        id="task-activation-date"
                                        value={form.activationDate}
                                        onChange={(value) => {
                                            clearFieldError('activation');
                                            setField('activationDate')(value);
                                        }}
                                        placeholder="Fecha"
                                        required
                                        error={formErrors.activation}
                                    />
                                    <TimePickerModern
                                        id="task-activation-time"
                                        value={form.activationTime}
                                        onChange={(value) => {
                                            clearFieldError('activation');
                                            setField('activationTime')(value);
                                        }}
                                        placeholder="Hora"
                                        daySlots
                                    />
                                </div>
                                <p className="task-form__hint">
                                    Los alumnos verán la tarea a partir de este momento.
                                </p>
                            </div>

                            <div className="task-form__datetime-block">
                                <span className="task-form__datetime-label">
                                    Fecha límite de entrega
                                    <span className="task-form__datetime-required" aria-hidden="true">*</span>
                                </span>
                                <div className="task-form__datetime-row">
                                    <InputDateModern
                                        id="task-due-date"
                                        value={form.dueDate}
                                        onChange={(value) => {
                                            clearFieldError('due');
                                            setField('dueDate')(value);
                                        }}
                                        placeholder="Fecha"
                                        minDate={form.activationDate}
                                        required
                                        error={formErrors.due}
                                    />
                                    <TimePickerModern
                                        id="task-due-time"
                                        value={form.dueTime}
                                        onChange={(value) => {
                                            clearFieldError('due');
                                            setField('dueTime')(value);
                                        }}
                                        placeholder="Hora"
                                        daySlots
                                    />
                                </div>
                            </div>
                        </CourseFormSection>

                        <CourseFormSection
                            id="task-section-attachments"
                            title="Archivos adjuntos"
                            description="Material de apoyo opcional para los alumnos."
                            icon={<PaperClipIcon className={SECTION_ICON_CLASS} />}
                        >
                            <TaskAttachmentField
                                label="Archivos (opcional)"
                                existing={existingAttachments}
                                onRemoveExisting={(_, index) => {
                                    setExistingAttachments((prev) => prev.filter((__, i) => i !== index));
                                }}
                                staged={stagedItems}
                                isUploading={isUploading}
                                onSelectFiles={handleSelectFiles}
                                onRemoveStaged={removeItem}
                                disabled={saving}
                                multiple
                                maxFiles={TASK_MAX_ATTACHMENTS}
                            />
                        </CourseFormSection>
                    </div>
                </div>
            </div>
        </DrawerCustom>
    );
};
